import React, { useEffect, useMemo, useState } from "react";
import useApi from "../../CustomHooks/useApi";
import { financeAPI } from "../../../api/apiClient";
import "../../../css/Finance/DraftVariantExpensesSection.css";

const VariantExpensePopup = ({
  isOpen,
  mode,
  variantName,
  expense,
  onClose,
  onSubmit,
  onDelete,
  isLoading,
  isDeleting,
}) => {
  const isEdit = mode === "edit";

  const [amount, setAmount] = useState("");
  const [title, setTitle] = useState("");
  const [groupKey, setGroupKey] = useState("");
  const [expenseDate, setExpenseDate] = useState("");
  const [notes, setNotes] = useState("");
  const [isInvoiceOnDrive, setIsInvoiceOnDrive] = useState(false);

  useEffect(() => {
    if (!isOpen) return;

    setAmount(expense?.amount?.toString() ?? "");
    setTitle(expense?.title ?? "");
    setGroupKey(expense?.groupKey ?? "");
    setExpenseDate(expense?.expenseDate ? expense.expenseDate.slice(0, 10) : "");
    setNotes(expense?.notes ?? "");
    setIsInvoiceOnDrive(!!expense?.isInvoiceOnDrive);
  }, [isOpen, expense]);

  const numericAmount = Number(amount);
  const isAmountValid =
    amount !== "" && !Number.isNaN(numericAmount) && numericAmount > 0;

  const canSubmit = !!title.trim() && isAmountValid && !isLoading && !isDeleting;
  

  const handleSubmit = () => {
    if (!canSubmit) return;

    onSubmit(
      {
        amount: numericAmount,
        title: title.trim(),
        groupKey: groupKey.trim() || null,
        expenseDate: expenseDate || null,
        notes: notes.trim() || null,
        isInvoiceOnDrive,
      },
      () => {
        setAmount("");
        setTitle("");
        setGroupKey("");
        setExpenseDate("");
        setNotes("");
        setIsInvoiceOnDrive(false);
      }
    );
  };

  if (!isOpen) return null;
  

  return (
    <div className="full-refund-popup-overlay" onClick={onClose}>
      <div
        className="full-refund-popup variant-expense-popup"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="full-refund-popup-header">
          <div>
            <div className="full-refund-popup-title">
              {isEdit ? "Edytuj koszt stowarzyszenia" : "Dodaj koszt stowarzyszenia"}
            </div>
            <div className="full-refund-popup-subtitle">
              Koszt przypisany do wariantu: <strong>{variantName}</strong>
            </div>
          </div>

          <div className="full-refund-popup-close" onClick={onClose}>
            <i className="fa-solid fa-xmark" />
          </div>
        </div>

        <div className="variant-expense-popup-body">
                      <div className="variant-expense-form-hint">
              W tej sekcji dodajesz tylko koszty stowarzyszenia, dlatego kwota
              musi być dodatnia. Jeśli wpiszesz tę samą etykietę w kilku
              kosztach, w widoku „Wszystkie warianty” zostaną pokazane łącznie.
            </div>
          <div className="variant-expense-form-card">

            <div className="variant-expense-form-fields">
              <input
                type="text"
                placeholder="Opis kosztu"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
              />

              <input
                type="number"
                step="0.01"
                min="0.01"
                placeholder="Kwota kosztu, np. 125.50"
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
              />

              {amount !== "" && !isAmountValid && (
                <div className="variant-expense-form-error">
                  W tym miejscu można dodać tylko dodatnią kwotę, ponieważ sekcja
                  służy do kosztów stowarzyszenia.
                </div>
              )}

              <input
                type="date"
                value={expenseDate}
                onChange={(e) => setExpenseDate(e.target.value)}
              />

              <input
                type="text"
                placeholder="Etykieta (opcjonalnie)"
                value={groupKey}
                onChange={(e) => setGroupKey(e.target.value)}
              />

              <textarea
                placeholder="Notatka (np. wykonano przelew)"
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                rows={3}
              />

              <label className="variant-expense-checkbox-row">
                <input
                  type="checkbox"
                  checked={isInvoiceOnDrive}
                  onChange={(e) => setIsInvoiceOnDrive(e.target.checked)}
                />
                <span>Faktura na dysku</span>
              </label>

              <div className="variant-expense-popup-actions">
                {isEdit && (
                  <div
                    className={`variant-expense-delete-btn ${
                      isLoading || isDeleting ? "disabled" : ""
                    }`}
                    onClick={() => {
                      if (!isLoading && !isDeleting) {
                        onDelete();
                      }
                    }}
                  >
                    {isDeleting ? (
                      <i className="fa fa-spinner fa-spin" />
                    ) : (
                      <>
                        <i className="fa-solid fa-xmark" />
                        <span>Usuń</span>
                      </>
                    )}
                  </div>
                )}

                <div
                  className={`full-refund-add-btn variant-expense-submit-btn ${
                    !canSubmit ? "disabled" : ""
                  }`}
                  onClick={handleSubmit}
                >
                  {isLoading ? (
                    <i className="fa fa-spinner fa-spin" />
                  ) : (
                    <>
                      <i className="fa-solid fa-check" />
                      <span>{isEdit ? "Zapisz zmiany" : "Dodaj koszt"}</span>
                    </>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

const DraftVariantExpensesSection = ({
  tripGuid,
  draft,
  groupedReservations,
  onDraftChanged,
  showToast,
  clearToast,
  canEdit
}) => {
  const { callApi, isLoading } = useApi();

  const [selectedVariantGuid, setSelectedVariantGuid] = useState("all");
  const [showAddPopup, setShowAddPopup] = useState(false);
  const [selectedExpense, setSelectedExpense] = useState(null);
  const [deletingExpenseId, setDeletingExpenseId] = useState(null);

  const variants = useMemo(() => {
  return Object.values(groupedReservations ?? {}).sort((a, b) =>
    (a.variantName || "").localeCompare(b.variantName || "", "pl", {
      sensitivity: "base",
    })
  );
}, [groupedReservations]);

const hasSingleVariant = variants.length === 1;

const selectedVariant = useMemo(() => {
  if (hasSingleVariant) {
    return variants[0] ?? null;
  }

  if (selectedVariantGuid === "all") return null;

  return variants.find((v) => v.variantGuid === selectedVariantGuid) || null;
}, [selectedVariantGuid, variants, hasSingleVariant]);

const popupVariantName = useMemo(() => {
  if (selectedExpense) {
    return selectedExpense.variantName ?? selectedVariant?.variantName ?? "—";
  }

  return selectedVariant?.variantName ?? "—";
}, [selectedExpense, selectedVariant]);

  const allExpenses = useMemo(() => {
    return (draft?.variantExpenses ?? []).map((item) => ({
      ...item,
      normalizedGroupKey: item.groupKey?.trim().toLowerCase() || null,
    }));
  }, [draft]);

  const aggregatedAllExpenses = useMemo(() => {
    const groups = {};

    allExpenses.forEach((item) => {
      const hasGroupKey = !!item.normalizedGroupKey;

      const key = hasGroupKey
        ? `group:${item.normalizedGroupKey}`
        : `expense:${item.id}`;

      const label = item.title || "Pozostałe";

      if (!groups[key]) {
        groups[key] = {
          key,
          label,
          amount: 0,
          count: 0,
          groupKey: item.groupKey?.trim() || null,
          variantNames: new Set(),
          sourceExpense: item,
        };
      }

      groups[key].amount += Number(item.amount || 0);
      groups[key].count += 1;

      if (item.variantName) {
        groups[key].variantNames.add(item.variantName);
      }
    });

    return Object.values(groups)
      .map((group) => ({
        ...group,
        variantNames: Array.from(group.variantNames).sort((a, b) =>
          (a || "").localeCompare(b || "", "pl", {
            sensitivity: "base",
          })
        ),
      }))
      .sort((a, b) => {
        const aDate = a.sourceExpense?.expenseDate;
        const bDate = b.sourceExpense?.expenseDate;
        if (aDate && bDate && aDate !== bDate) return aDate.localeCompare(bDate);
        if (aDate && !bDate) return -1;
        if (!aDate && bDate) return 1;
        return (a.label || "").localeCompare(b.label || "", "pl", { sensitivity: "base" });
      });
  }, [allExpenses]);

  const selectedVariantExpenses = useMemo(() => {
    if (!selectedVariant) return [];

    return allExpenses
      .filter((item) => item.variantGuid === selectedVariant.variantGuid)
      .sort((a, b) => {
        if (a.expenseDate && b.expenseDate && a.expenseDate !== b.expenseDate) return a.expenseDate.localeCompare(b.expenseDate);
        if (a.expenseDate && !b.expenseDate) return -1;
        if (!a.expenseDate && b.expenseDate) return 1;
        return (a.title || "").localeCompare(b.title || "", "pl", { sensitivity: "base" });
      });
  }, [allExpenses, selectedVariant]);

  const totalAllExpenses = useMemo(() => {
    return allExpenses.reduce((sum, item) => sum + Number(item.amount || 0), 0);
  }, [allExpenses]);

  const totalSelectedVariantExpenses = useMemo(() => {
    return selectedVariantExpenses.reduce(
      (sum, item) => sum + Number(item.amount || 0),
      0
    );
  }, [selectedVariantExpenses]);

  const formatDate = (value) => {
    if (!value) return null;
    return new Date(value).toLocaleDateString("pl-PL");
  };

  const closeExpensePopup = () => {
    if (isLoading || deletingExpenseId) return;

    setShowAddPopup(false);
    setSelectedExpense(null);
  };

  const handleAddExpense = (payload, onDone) => {
    if (!selectedVariant) return;

    callApi(
      () =>
        financeAPI.addDraftVariantExpense(tripGuid, {
          variantGuid: selectedVariant.variantGuid,
          amount: payload.amount,
          title: payload.title,
          groupKey: payload.groupKey,
          expenseDate: payload.expenseDate,
          notes: payload.notes,
          isInvoiceOnDrive: payload.isInvoiceOnDrive,
        }),
      async () => {
        await onDraftChanged();
        if (onDone) onDone();

        setShowAddPopup(false);
        showToast("Dodano koszt stowarzyszenia", "success");
      },
      () => clearToast()
    );
  };

  const handleUpdateExpense = (payload, onDone) => {
    if (!selectedExpense) return;

    callApi(
      () =>
        financeAPI.updateDraftVariantExpense(
          tripGuid,
          selectedExpense.id,
          {
            amount: payload.amount,
            title: payload.title,
            groupKey: payload.groupKey,
            expenseDate: payload.expenseDate,
            notes: payload.notes,
            isInvoiceOnDrive: payload.isInvoiceOnDrive,
          }
        ),
      async () => {
        await onDraftChanged();
        if (onDone) onDone();

        setSelectedExpense(null);
        showToast("Zaktualizowano koszt stowarzyszenia", "success");
      },
      () => clearToast()
    );
  };

  const handleSubmitExpense = (payload, onDone) => {
    if (selectedExpense) {
      handleUpdateExpense(payload, onDone);
      return;
    }

    handleAddExpense(payload, onDone);
  };

  const handleDeleteExpense = (expenseId) => {
    setDeletingExpenseId(expenseId);

    callApi(
      () => financeAPI.deleteDraftVariantExpense(tripGuid, expenseId),
      async () => {
        await onDraftChanged();
        setDeletingExpenseId(null);
        setSelectedExpense(null);
        setShowAddPopup(false);
        showToast("Usunięto koszt stowarzyszenia", "success");
      },
      () => {
        clearToast();
        setDeletingExpenseId(null);
      }
    );
  };

  return (
    <div className="draft-card full-refund-picker-card">
      <div className="draft-card-title">4. Koszty stowarzyszenia</div>

      {canEdit && <>
      <div className="draft-card-subtitle">
        Wybierz wariant, aby dodać koszt związany z realizacją usługi (nocleg,
        przejazd, atrakcja). Aby podzielić koszt na kilka wariantów użyj tę samą
        etykietę, aby w widoku Wszystkie warianty zsumować koszty za tę usługę.
      </div>

      <div className="variant-expenses-toolbar">
      {!hasSingleVariant && (
        <select
          className="variant-expenses-select"
          value={selectedVariantGuid}
          onChange={(e) => setSelectedVariantGuid(e.target.value)}
        >
          <option value="all">Wszystkie warianty</option>

          {variants.map((variant) => (
            <option key={variant.variantGuid} value={variant.variantGuid}>
              {variant.variantName}
            </option>
          ))}
        </select>
      )}

        {selectedVariant && (
          <div
            className="full-refund-add-btn"
            onClick={() => setShowAddPopup(true)}
          >
            <i className="fa-solid fa-plus" />
            <span>Dodaj koszt</span>
          </div>
        )}


        <div className="full-refund-picker-count">
          Łącznie:{" "}
          <strong>
            {Number(
              selectedVariant ? totalSelectedVariantExpenses : totalAllExpenses
            ).toFixed(2)}{" "}
            zł
          </strong>
        </div>
      </div>
              </> }

      {selectedVariant === null ? (
        <div className="variant-expenses-summary-list">
          {aggregatedAllExpenses.length === 0 ? (
            <div className="draft-empty">Brak kosztów stowarzyszenia.</div>
          ) : (
            aggregatedAllExpenses.map((item) => (
              <div
                key={item.key}
                className="variant-expense-summary-row"
              >
                <div className="variant-expense-summary-left">
                  <div className="variant-expense-summary-name">
                    <i className="fa-solid fa-receipt variant-expense-icon" />
                    {item.label}{" "}
                    {item.groupKey && (
                      <span className="variant-expense-detail-meta">
                        #{item.groupKey}
                      </span>
                    )}
                  </div>

                  {item.variantNames?.length > 0 && (
                    <div className="variant-expense-summary-meta">
                      <span className="variant-expense-count">
                        {item.variantNames
                          .map((name) => `[${name}]`)
                          .join(" | ")}
                      </span>
                    </div>
                  )}
                </div>

                <div className="variant-expense-summary-amount">
                  {Number(item.amount).toFixed(2)} zł
                </div>
              </div>
            ))
          )}
        </div>
      ) : (
        <div className="variant-expenses-details-list">
          {selectedVariantExpenses.length === 0 ? (
            <div className="draft-empty">
              Brak kosztów dla wariantu {selectedVariant.variantName}.
            </div>
          ) : (
            selectedVariantExpenses.map((item) => {
              const isDeleting = deletingExpenseId === item.id;

              return (
                <div
                  key={item.id}
                  className={`variant-expense-detail-row ${
                    isDeleting ? "is-saving" : ""
                  }`}
                  onClick={() => {
                    if (!isDeleting && !isLoading) {
                      setSelectedExpense(item);
                    }
                  }}
                >
                  <div className="variant-expense-detail-left">
                    <div className="variant-expense-detail-name">
                      <i className="fa-solid fa-receipt variant-expense-icon" />{" "}
                      {item.title || "—"}
                      {item.groupKey && (
                        <span className="variant-expense-detail-meta">
                          #{item.groupKey}
                        </span>
                      )}
                    </div>

                    <div className="variant-expense-detail-extra">
                      {item.expenseDate && (
                        <span>
                          <i className="fa-regular fa-calendar" />{" "}
                          {formatDate(item.expenseDate)}
                        </span>
                      )}

                      {item.notes && (
                        <span>
                          <i className="fa-regular fa-note-sticky" />{" "}
                          {item.notes}
                        </span>
                      )}

                      {item.isInvoiceOnDrive && (
                        <span>
                          <i className="fa-brands fa-google-drive" /> Faktura na
                          dysku
                        </span>
                      )}
                    </div>
                  </div>

                  <div className="variant-expense-detail-right">
                    <div className="amount-positive">
                      {Number(item.amount).toFixed(2)} zł
                    </div>

                    {canEdit && <div className="variant-expense-edit-hint">
                      <i className="fa-solid fa-pen" />
                    </div> }
                  </div>
                </div>
              );
            })
          )}
        </div>
      )}

      {canEdit && <VariantExpensePopup
        isOpen={showAddPopup || !!selectedExpense}
        mode={selectedExpense ? "edit" : "add"}
        variantName={popupVariantName}
        expense={selectedExpense}
        onClose={closeExpensePopup}
        onSubmit={handleSubmitExpense}
        onDelete={() => {
          if (selectedExpense) {
            handleDeleteExpense(selectedExpense.id);
          }
        }}
        isLoading={isLoading}
        isDeleting={!!deletingExpenseId}
      /> }
    </div>
  );
};

export default DraftVariantExpensesSection;